/* The Copilot conversation, shared by the drawer and the `/copilot` page.
 *
 * **One conversation, wherever it is read**, which is the design's model: a question asked in the
 * drawer is on the page when the reader opens the full Copilot, and the other way round. Two threads
 * would mean the same reader seeing two different histories of one session depending on which door
 * they came through.
 *
 * **Held in memory for the tab, never persisted.** The server's transcript is the record — every
 * question is stored with the outcome it produced — and "Recent questions" reads it. This is only the
 * conversation a reader is having now, so a reload starting it afresh is right, and nothing here can
 * disagree with the transcript about what was asked.
 *
 * A plain store with `useSyncExternalStore`-shaped functions rather than a React context, so the
 * drawer (mounted in the shell) and the page (mounted by the router) share it without either having
 * to be the other's ancestor.
 */

import type { AnswerFields } from '@/shared/api/generated/ai-copilot';

/** An answer as the thread shows it — a fresh ask, or a stored transcript row opened from history.
 *
 * Only a fresh ask carries what the model was shown (`context_preview`) and how many records that
 * was; a stored row does not, so both are optional and the card says nothing about a context it
 * was never given. */
export interface ShownAnswer extends AnswerFields {
  readonly snippet_count?: number;
  readonly context_preview?: string;
  readonly asked_at?: string;
}

export type Turn =
  | { readonly id: string; readonly question: string; readonly state: 'pending' }
  | {
      readonly id: string;
      readonly question: string;
      readonly state: 'answered';
      readonly answer: ShownAnswer;
    }
  | {
      readonly id: string;
      readonly question: string;
      readonly state: 'failed';
      readonly message: string;
    };

let turns: readonly Turn[] = [];
const listeners = new Set<() => void>();

export function threadSnapshot(): readonly Turn[] {
  return turns;
}

export function subscribeThread(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function updateThread(next: (current: readonly Turn[]) => readonly Turn[]): void {
  turns = next(turns);
  for (const listener of listeners) listener();
}

/** "New conversation". Also what a test calls between cases, since the store outlives a render. */
export function clearThread(): void {
  updateThread(() => []);
}

/** Whether a question is in flight, from either door. One at a time, so two answers cannot
 * arrive out of order against one conversation. */
export function isAsking(current: readonly Turn[]): boolean {
  return current.some((turn) => turn.state === 'pending');
}
